import { useState, useEffect } from "react";
import api from "../../../utils/api";

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    OutlinedInput,
    FormHelperText,
    Button,
    Box,
    FormControl,
    FormLabel,
    CircularProgress,
    IconButton,
    Typography,
} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';

const languages = ["en", "hy", "ru"];
const languageLabels = { en: "English", hy: "Armenian", ru: "Russian" };
const MAX_IMAGES = 10;

export default function ExamScoreModal({ open, onClose, row = null }) {
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const [form, setForm] = useState({
        title_en: "",
        title_hy: "",
        title_ru: "",
        date: "",
    });
    const [existingImages, setExistingImages] = useState([]);
    const [newFiles, setNewFiles] = useState([]);
    const [newPreviews, setNewPreviews] = useState([]);

    const totalImages = existingImages.length + newFiles.length;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleImageChange = (e) => {
        const files = Array.from(e.target.files || []).filter((file) =>
            (file.type || "").startsWith("image/")
        );
        e.target.value = "";
        if (files.length === 0) return;

        const room = MAX_IMAGES - existingImages.length - newFiles.length;
        if (room <= 0) {
            setErrors((prev) => ({
                ...prev,
                images: [`You can upload at most ${MAX_IMAGES} images.`],
            }));
            return;
        }

        const accepted = files.slice(0, room);
        const nextFiles = [...newFiles, ...accepted];
        setNewFiles(nextFiles);
        setErrors((prev) => {
            const next = { ...prev };
            delete next.images;
            return next;
        });

        accepted.forEach((file) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                setNewPreviews((prev) => [...prev, reader.result]);
            };
            reader.readAsDataURL(file);
        });
    };

    const handleRemoveExisting = (index) => {
        setExistingImages((prev) => prev.filter((_, i) => i !== index));
    };

    const handleRemoveNew = (index) => {
        setNewFiles((prev) => prev.filter((_, i) => i !== index));
        setNewPreviews((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async () => {
        if (totalImages < 1) {
            setErrors({ images: ['At least one image is required.'] });
            return;
        }

        try {
            setSaving(true);
            setErrors({});

            const formData = new FormData();
            formData.append('title_en', form.title_en || '');
            formData.append('title_hy', form.title_hy || '');
            formData.append('title_ru', form.title_ru || '');
            formData.append('date', form.date);

            newFiles.forEach((file) => {
                formData.append('images[]', file);
            });

            if (row?.id) {
                formData.append('keep_images', JSON.stringify(existingImages));
                await api.post(`admin/exam-scores/${row.id}/update`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });
            } else {
                await api.post("admin/exam-scores", formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });
            }

            onClose(true);
        } catch (e) {
            if (e.errors) {
                setErrors(e.errors);
            } else if (e.response?.data?.errors) {
                setErrors(e.response.data.errors);
            }
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        if (row) {
            setForm({
                title_en: row.title_en || "",
                title_hy: row.title_hy || "",
                title_ru: row.title_ru || "",
                date: row.date ? new Date(row.date).toISOString().split('T')[0] : "",
            });
            const images = Array.isArray(row.images) && row.images.length
                ? row.images
                : (row.image ? [row.image] : []);
            setExistingImages(images);
        } else {
            setForm({
                title_en: "",
                title_hy: "",
                title_ru: "",
                date: "",
            });
            setExistingImages([]);
        }
        setNewFiles([]);
        setNewPreviews([]);
        setErrors({});
        setSaving(false);
    }, [row, open]);

    const imageError = errors.images?.[0] || errors['images.0']?.[0] || errors.image?.[0];

    return (
        <Dialog open={open} onClose={() => onClose()} maxWidth="sm" fullWidth>
            <DialogTitle>{!row ? "Create Exam Score" : "Edit Exam Score"}</DialogTitle>
            <DialogContent>
                <Box display="flex" flexDirection="column" gap={2} mt={1}>
                    <FormControl error={!!imageError}>
                        <FormLabel>Images *</FormLabel>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                            Up to {MAX_IMAGES} images. First image is the cover.
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
                            {existingImages.map((src, index) => (
                                <Box key={`existing-${src}-${index}`} sx={{ position: 'relative' }}>
                                    <Box
                                        component="img"
                                        src={src}
                                        alt=""
                                        sx={{
                                            width: 96,
                                            height: 96,
                                            objectFit: 'cover',
                                            borderRadius: 1,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                        }}
                                    />
                                    <IconButton
                                        size="small"
                                        color="error"
                                        sx={{
                                            position: 'absolute',
                                            top: -8,
                                            right: -8,
                                            bgcolor: 'rgba(255, 255, 255, 0.9)',
                                        }}
                                        onClick={() => handleRemoveExisting(index)}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            ))}
                            {newPreviews.map((src, index) => (
                                <Box key={`new-${index}`} sx={{ position: 'relative' }}>
                                    <Box
                                        component="img"
                                        src={src}
                                        alt=""
                                        sx={{
                                            width: 96,
                                            height: 96,
                                            objectFit: 'cover',
                                            borderRadius: 1,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                        }}
                                    />
                                    <IconButton
                                        size="small"
                                        color="error"
                                        sx={{
                                            position: 'absolute',
                                            top: -8,
                                            right: -8,
                                            bgcolor: 'rgba(255, 255, 255, 0.9)',
                                        }}
                                        onClick={() => handleRemoveNew(index)}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            ))}
                            {totalImages < MAX_IMAGES && (
                                <Button variant="outlined" component="label">
                                    {totalImages ? 'Add Images' : 'Upload Images'}
                                    <input
                                        type="file"
                                        hidden
                                        accept="image/*"
                                        multiple
                                        onChange={handleImageChange}
                                    />
                                </Button>
                            )}
                        </Box>
                        {imageError && (
                            <FormHelperText sx={{ m: 0.5 }} error>
                                {imageError}
                            </FormHelperText>
                        )}
                    </FormControl>

                    {languages.map((lang) => (
                        <FormControl key={lang} error={!!errors[`title_${lang}`]?.[0]}>
                            <FormLabel htmlFor={`title_${lang}`}>Title ({languageLabels[lang]})</FormLabel>
                            <OutlinedInput
                                name={`title_${lang}`}
                                fullWidth
                                value={form[`title_${lang}`]}
                                onChange={handleChange}
                            />
                            {errors[`title_${lang}`] && (
                                <FormHelperText sx={{ m: 0.5 }} error>
                                    {errors[`title_${lang}`][0]}
                                </FormHelperText>
                            )}
                        </FormControl>
                    ))}

                    <FormControl error={!!errors.date?.[0]}>
                        <FormLabel htmlFor="date">Date *</FormLabel>
                        <OutlinedInput
                            name="date"
                            type="date"
                            fullWidth
                            value={form.date}
                            onChange={handleChange}
                        />
                        {errors.date && (
                            <FormHelperText sx={{ m: 0.5 }} error={!!errors.date}>
                                {errors.date[0]}
                            </FormHelperText>
                        )}
                    </FormControl>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onClose()}>Cancel</Button>
                <Button
                    sx={{ color: '#fff' }}
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={saving}
                >
                    {saving ? <CircularProgress size={24} /> : (!row ? "Create" : "Update")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
